import { Phone, Eye, Glasses, Zap, Droplet, Sparkles, Check } from "lucide-react";
import { CONTACT, SERVICES } from "../data/site";
import Reveal from "../components/Reveal";
import { PageHero, CallToAction } from "../components/Sections";

const iconMap = { Eye, Glasses, Zap, Droplet, Sparkles };

const highlights = {
  "eye-exams": ["Comprehensive vision & eye health exams", "Dedicated diabetic eye exams", "Advanced diagnostic technology"],
  "eyeglasses": ["Designer & everyday frames", "Anti-glare & blue-light protection", "Progressive & digital lenses"],
  "glasses-in-an-hour": ["On-site finishing lab", "Many glasses ready in about an hour", "Restrictions apply"],
  "dry-eye": ["Gentle radiofrequency technology", "Personalized treatment plans", "Lasting comfort & relief"],
  "tempsure": ["Non-invasive radiofrequency", "Reduces fine lines & rebuilds collagen", "Natural-looking, no downtime"],
};

const CHECK_ICON_SIZE = 13;

const HighlightList = ({ items }) => (
  <ul className="space-y-3 mb-8">
    {items.map((h) => (
      <li key={h} className="flex items-center gap-3 text-sm text-foreground">
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-tiffany/15 shrink-0">
          <Check size={CHECK_ICON_SIZE} className="text-tiffany" />
        </span>
        {h}
      </li>
    ))}
  </ul>
);

const ServiceSection = ({ service, index }) => {
  const Icon = iconMap[service.icon];
  const reversed = index % 2 === 1;
  return (
    <section
      id={service.id}
      data-testid={`service-section-${service.id}`}
      className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center scroll-mt-28"
    >
      <Reveal className={reversed ? "lg:order-2" : ""}>
        <div className="relative overflow-hidden rounded-sm">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-[420px] object-cover"
          />
        </div>
      </Reveal>
      <Reveal delay={0.12} className={reversed ? "lg:order-1" : ""}>
        <div>
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-tiffany/10 mb-6">
            <Icon className="text-tiffany" size={28} />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-navy mb-5">
            {service.title}
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed mb-7">
            {service.description}
          </p>
          <HighlightList items={highlights[service.id]} />
          <a
            href={CONTACT.phoneHref}
            data-testid={`service-call-${service.id}`}
            className="inline-flex items-center gap-2 text-navy font-semibold hover:text-tiffany transition-colors"
          >
            <Phone size={18} /> Call to learn more
          </a>
        </div>
      </Reveal>
    </section>
  );
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Our Services"
        title="Expert eye care, eyewear & wellness"
        subtitle="From comprehensive and diabetic eye exams to glasses in about an hour and TempSure treatments — discover everything our practice offers."
        image={SERVICES[0].image}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28 space-y-24 sm:space-y-32">
        {SERVICES.map((s, i) => (
          <ServiceSection key={s.id} service={s} index={i} />
        ))}
      </div>
      <CallToAction
        title="Have Questions About a Service?"
        subtitle="Our friendly team is happy to help. Call us and we'll guide you to the right care."
      />
    </div>
  );
}
